/**
 * Constants for creating a scheduled job.
 */
export const createScheduledConstants = {

  /**
   * The time format for DD/MM/YYYY.
   */
  time_format_dd_mm_yy: 'DD/MM/YYYY',
  /**
   * The time format for MM/DD/YYYY.
   */
  time_format_mm_dd_yy: 'MM/DD/YYYY',
  /**
   * The email of the user who created the job.
   */
  created_by: 'user@pwc.com',
  /**
   * The maximum length for the job name.
   */
  jobNameMaxLength: 50
};

/**
 * Represents the validation states for a job.
 */
export enum jobValidation {
  /**
   * The job is valid.
   */
  VALID = 'VALID',
  /**
   * The job is invalid.
   */
  INVALID = 'INVALID',
  /**
   * The job name length exceeds the maximum length.
   */
  LENGTH_EXCEEDED = 'LENGTH_EXCEEDED',
  /**
   * The job name is empty.
   */
  EMPTY = 'EMPTY'
}

/**
 * Represents the frequency types for a job.
 */
export enum typeFrequency {
  /**
   * The job frequency is hourly.
   */
  HOURLY = 'hourly',
  /**
   * The job frequency is daily.
   */
  DAILY = 'daily',
  /**
   * The job frequency is weekly.
   */
  WEEKLY = 'weekly',
  /**
   * The job frequency is monthly.
   */
  MONTHLY = 'monthly'
}

/**
 * Represents the loading states for data.
 */
export enum DataLoadingState {
  /**
   * The data is loading.
   */
  LOADING = 'LOADING',
  /**
   * The data loading is completed.
   */
  COMPLETED = 'COMPLETED',
  /**
   * The data loading has failed.
   */
  FAILED = 'FAILED'
}

/**
 * Represents the sort order for API modification.
 */
export enum ApiModifySort {
  /**
   * The sort order is ascending.
   */
  ASC = 'ASC',
  /**
   * The sort order is descending.
   */
  DESC = 'DESC'
}

/**
 * Represents the sort labels for scheduler fields.
 */
export const SortLabels = {
  /**
   * The sort label for scheduler name.
   */
  SCHEDULER_NAME: 'jobName',
  /**
   * The sort label for scheduler status.
   */
  SCHEDULER_STATUS: 'status',
  /**
   * The sort label for scheduler frequency.
   */
  SCHEDULER_FREQUENCY: 'frequency',
  /**
   * The sort label for last modified timestamp.
   */
  LAST_MODIFIED: 'updatedTimestamp',
  /**
   * The sort label for updated by user.
   */
  UPDATED_BY: 'updatedBy',
  /**
   * Label for the action button.
   */
  ACTION_BTN: 'actionBtn',
};

/**
 * Represents the details of a scheduler cron.
 */
export interface ISchedulerCronDetails {
  /**
   * The frequency type of the scheduler.
   */
  frequencyType: string;
  /**
   * The frequency value of the scheduler (optional).
   */
  frequency?: number;
  /**
   * The hour at which the scheduler starts.
   */
  startsAtHour: number;
  /**
   * The minutes at which the scheduler starts (optional).
   */
  startsAtMinutes?: number;
  /**
   * The seconds at which the scheduler starts (optional).
   */
  startsAtSeconds?: number;
  /**
   * The hour at which the scheduler ends.
   */
  endsAtHour: number;
  /**
   * The minutes at which the scheduler ends (optional).
   */
  endsAtMinutes?: number;
  /**
   * The seconds at which the scheduler ends (optional).
   */
  endsAtSeconds?: number;
  /**
   * Indicates whether weekends should be skipped (optional).
   */
  skipWeekend?: boolean;
  /**
   * The days of the week for the scheduler (optional).
   */
  days?: string;
  /**
   * The day of the month for the scheduler (optional).
   */
  dayOfMonth?: number;
  /**
   * The day of the week for the scheduler (optional).
   */
  dayOfWeek?: string;
  /**
   * The week of the month for the scheduler (optional).
   */
  weekOfMonth?: number;
  /**
   * The month of the year for the scheduler (optional).
   */
  monthOfYear?: number;
  /**
   * The start date for the scheduler (optional).
   */
  startDate?: string;
  /**
   * The end date for the scheduler (optional).
   */
  endDate?: string;
}

/**
 * Represents the request payload for creating a job scheduler.
 */
export interface IJobSchedulerRequest {
  /**
   * The name of the job.
   */
  jobName: string;
  /**
   * The description of the job (optional).
   */
  jobDescription?: string;
  /**
   * The name of the journey.
   */
  journeyName: string;
  /**
   * The version of the journey.
   */
  journeyVersion: number;
  /**
   * The email of the user who created the job.
   */
  createdBy: string;
  /**
   * The name of the journey step.
   */
  journeyStepName: string;
  /**
   * The name of the event.
   */
  eventName: string;
  /**
   * The version of the event.
   */
  eventVersion: number;
  /**
   * The frequency of the job.
   */
  frequency: string;
  /**
   * The payload for the event (optional).
   */
  eventPayload?: object;
  /**
   * The cron details for the scheduler.
   */
  schedulerCronDetails: ISchedulerCronDetails;
}

/**
 * Represents the success response for a job scheduler API call.
 */
export interface IJobSchedulerSuccessResponse {
  /**
   * The version of the job.
   */
  version: number;
  /**
   * The ID of the job.
   */
  jobId: string;
  /**
   * The name of the job.
   */
  jobName: string;
  /**
   * The type of the job.
   */
  jobType: string;
}

/**
 * Represents the error response for a job scheduler API call.
 */
export interface IJobSchedulerErrorResponse {
  /**
   * The error code of the response.
   */
  errorCode: number;
  /**
   * The error message of the response (optional).
   */
  errorMessage?: string;
  /**
   * The timestamp of the response (optional).
   */
  timestamp?: string;
}

/**
 * Represents the request payload for getting jobs.
 */
export interface IGetJobsRequest {
  /**
   * The search key for filtering jobs (optional).
   */
  jobSearchKey?: string;
  /**
   * The name of the journey.
   */
  journeyName: string;
  /**
   * Version of the journey.
   */
  journeyVersion: number;
}

/**
 * Represents the details of a job.
 */
export interface IGetJobDetails {
  /**
   * The version of the job.
   */
  version: number;
  /**
   * The name of the job.
   */
  jobName: string;
  /**
   * The description of the job (optional).
   */
  jobDescription?: string;
  /**
   * The email of the user who created the job.
   */
  createdBy: string;
  /**
   * The timestamp when the job was created.
   */
  createdTimeStamp: string;
  /**
   * The timestamp when the job was last updated.
   */
  updatedTimestamp: string;
  /**
   * The email of the user who last updated the job.
   */
  updatedBy: string;
  /**
   * The name of the event.
   */
  eventName: string;
  /**
   * The name of the journey.
   */
  journeyName: string;
  /**
   * The frequency of the job (optional).
   */
  frequency?: string;
  /**
   * The payload for the event.
   */
  eventPayload: object;
  /**
   * The ID of the job.
   */
  jobId: string;
  /**
   * The instance count of the job.
   */
  jobInstanceCount: number;
  /**
   * The type of the job.
   */
  jobType: string;
  /**
   * The status of the job.
   */
  jobStatus: string;
}

/**
 * Represents the response payload for getting jobs.
 */
export interface IGetJobsResponse {
  /**
   * The total count of jobs.
   */
  totalJobCount: number,
  /**
   * The details of the jobs.
   */
  jobDetails: IGetJobDetails[]
}

/**
 * Represents the props for an event payload component.
 */
export interface IEventPayloadProps {
  /**
   * The value of the event payload.
   */
  value: string;
  /**
   * Indicates whether the event payload is valid.
   */
  isValidEventPayload: boolean;
  /**
   * Indicates whether the event payload is required.
   */
  eventPayloadRequired: boolean;
  /**
   * The change event handler for the input.
   * @param e - The change event.
   */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Represents the props for a job scheduler component.
 */
export interface IJobSchedulerProps {
  /**
   * The start date for the scheduler.
   */
  startDate: Date | null;
  /**
   * The change event handler for the start date.
   * @param date - The selected start date.
   */
  startDateHandler: (date: Date | null) => void;
  /**
   * The hour value for the start time.
   */
  startHour: string | number;
  /**
   * The minute value for the start time.
   */
  startMinute: string | number;
  /**
   * The change event handler for the start hour.
   * @param e - The change event.
   */
  startHourHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The change event handler for the start minute.
   * @param e - The change event.
   */
  startMinuteHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The blur event handler for the input fields.
   * @param e - The blur event.
   */
  blurHandler: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  /**
   * The key up event handler for the input fields.
   * @param e - The key up event.
   */
  keyUpHandler: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * The key down event handler for the input fields.
   * @param e - The key down event.
   */
  keydownHandler: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * The repetition value for the scheduler.
   */
  repetition: number;
  /**
   * The schedule value for the scheduler.
   */
  schedule: undefined | string;
  /**
   * The change event handler for the schedule.
   * @param e - The change event.
   */
  scheduleHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The end date for the scheduler.
   */
  endDate: Date | null;
  /**
   * The change event handler for the end date.
   * @param date - The selected end date.
   */
  endDateHandler: (date: Date | null) => void;
  /**
   * The hour value for the end time.
   */
  endHour: string | number;
  /**
   * The minute value for the end time.
   */
  endMinute: string | number;
  /**
   * The change event handler for the end hour.
   * @param e - The change event.
   */
  endHourHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The change event handler for the end minute.
   * @param e - The change event.
   */
  endMinuteHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The selected week days for the scheduler.
   */
  weekDays: string[];
  /**
   * The function to set the selected week days.
   * @param arr - The array of selected week days.
   */
  setWeekDays: (arr: string[]) => void;
  /**
   * The day of the month for the scheduler.
   */
  monthDay: number;
  /**
   * The change event handler for the month day.
   * @param arg - The selected month day.
   */
  monthDayHandler: (arg: number) => void;
  /**
   * The selected week of the month for the scheduler.
   */
  listWeek: string | number;
  /**
   * The change event handler for the week of the month.
   * @param e - The change event.
   */
  listWeekHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * The selected days of the week for the scheduler.
   */
  listDays: string;
  /**
   * The change event handler for the days of the week.
   * @param e - The change event.
   */
  listDaysHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * Indicates whether the month radio button is selected.
   */
  monthRadio: boolean;
  /**
   * The click event handler for the month radio button.
   */
  monthRadioHandler: () => void;
}

/**
 * Represents the props for creating a schedule.
 */
export interface ICreateScheduleProps {
  /**
   * The function to set the cancel flag.
   * @param arg - The cancel flag value.
   */
  setCancel: (arg: boolean) => void;
  /**
   * The name of the journey.
   */
  journeyName: string;
  /**
   * The name of the journey step.
   */
  journeyStepName: string;
  /**
   * The version of the journey.
   */
  journeyVersion: number;
  /**
   * The name of the event.
   */
  eventName: string;
  /**
   * The version of the event.
   */
  eventVersion: number;
  /**
   * The function to set the API success flag.
   * @param arg - The API success flag value.
   */
  setApiSuccess: (arg: boolean) => void;
}

/**
 * Represents the props for a schedule list.
 */
export interface IScheduleListProps {
  /**
   * Indicates whether the flag is set.
   */
  flag: boolean;
  /**
   * The function to handle delete scheduler.
   * @param journeyName - The journey name.
   */
  deleteScheduler: (journeyName: string, jobName: string) => void
  /**
   * The search term for filtering schedules.
   */
  searchTerm: string;
  /**
   * The name of the journey.
   */
  journeyName: string;
  /**
   * Version of the journey.
   */
  journeyVersion: number;
  /**
   * The current page number.
   */
  page: number;
  /**
   * The function to set the page number.
   * @param num - The page number.
   */
  setPage: (num: number) => void;
}

/**
 * Represents the props for a schedule component.
 */
export interface ScheduleProps {
  /**
   * Indicates whether the scheduler modal is open.
   */
  schedulerModalFlag: boolean,
  /**
   * The function to close the scheduler modal.
   */
  closeSchedulerModal: () => void,
  /**
   * The name of the journey.
   */
  journeyName: string,
  /**
   * The version of the journey.
   */
  journeyVersion: number;
  /**
   * The name of the journey step.
   */
  journeyStepName: string,
  /**
   * The name of the event.
   */
  eventName: string,
  /**
   * The version of the event.
   */
  eventVersion: number
}
